import { TZDateMini } from '@date-fns/tz';
import { format, parse } from 'date-fns';

import { parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { checkIfValueTypeAlignment } from './selection-helpers.js';
import { isExpressionString } from '../forms-core/time/timeParser.js';

function sanitizeCopyString(selectionString: string): string {
  return selectionString
    .split('→')
    .map((part) => part.replaceAll('\n', '').trim())
    .join(' ')
    .trim();
}

export function convertCopyString(
  selectionString: string,
  patterns: Array<string> | null,
): string {
  const matchableDateTime: string[] = selectionString
    .split('→')
    .map((dateTime) => dateTime.replace(/\n/g, '').trim());

  const parsedDateTimes: string[] = [];
  try {
    for (const [index, dateTimeInput] of matchableDateTime.entries()) {
      // If a expression is detected, we simply return the original selection string
      // without the → separator
      if (isExpressionString(dateTimeInput)) {
        // With parsing the time as time value we make sure that the copied expression
        // is valid grail format.
        parsedDateTimes[index] =
          parseTimeAsTimeValue(dateTimeInput)?.value ?? dateTimeInput;

        continue;
      }

      // Check if the dateTimeInput is parsable by the DtDate
      const currentPattern = patterns?.[index];

      if (
        currentPattern === undefined ||
        (currentPattern.includes('dd') &&
          currentPattern.includes('MM') &&
          currentPattern.includes('yyyy')) === false
      ) {
        // We will need at least day, month and year to create a valid ISO date.
        parsedDateTimes[index] = dateTimeInput;
        continue;
      }

      // Try to parse the date with the correct pattern and timezone
      const dateTimeFormat = parse(
        dateTimeInput,
        currentPattern,
        new TZDateMini(0, getTimezone()),
      );

      // Define our precise ISO format, because the removePrecision function moves to a `Z` timezone, while we want to keep the user defined one.
      let precisionPattern = "yyyy-MM-dd'T'HH:mm:ss.SSSXXX";
      if (currentPattern.includes('ss')) {
        precisionPattern = "yyyy-MM-dd'T'HH:mm:ssXXX";
      } else if (currentPattern.includes('mm')) {
        precisionPattern = "yyyy-MM-dd'T'HH:mmXXX";
      } else if (
        currentPattern.includes('dd') &&
        currentPattern.includes('MM') &&
        currentPattern.includes('yyyy')
      ) {
        precisionPattern = 'yyyy-MM-dd';
      }
      parsedDateTimes[index] = format(dateTimeFormat, precisionPattern);
    }
  } catch {
    // If any error occurs during parsing, we fallback to the original selection string without the → separator
    return sanitizeCopyString(selectionString);
  }

  // If both date times were successfully parsed, we join them with a / to create a valid timeframe string.
  if (parsedDateTimes.length > 0) {
    return parsedDateTimes.filter(Boolean).join('/');
  }

  return sanitizeCopyString(selectionString);
}

/**
 * Helper function that builds a pattern array from the selected text nodes.
 * Extracts patterns from data-pattern attributes and skips data-expression nodes.
 *
 * Returns the pattern and the raw selected text.
 *
 * Both getRangeAt(0)+intersectsNode and selection.containsNode are unreliable for detecting which nodes are visually
 * selected when the selection spans multiple contenteditable elements. Instead, we use the treeWalker to go over all
 * nodes in the inputgroup, accumulating text and patterns while filtering out nodes that don't align with the
 * value type (e.g. skipping spin button nodes when in expression mode).
 * `contenteditable` needs to stay on the elements to avoid losing the focused element.
 * @internal
 */
export function getPatternAndSelectedText(selection: Selection): {
  pattern: string[] | null;
  text: string;
} {
  if (selection.rangeCount === 0) {
    return { pattern: null, text: '' };
  }

  // Use getRangeAt(0) instead of anchorNode/focusNode + selection.direction.
  // A Range always has startContainer ≤ endContainer in document order, so we
  // never need to guess the direction.
  //
  // Why not selection.direction:
  // Safari reports direction === 'none' for selections created via addRange()
  // when no contenteditable element is active (e.g. after we temporarily strip
  // [contenteditable] to allow Safari's addRange to cross element boundaries).
  // With direction 'none', the old code treated focusNode as the start and
  // anchorNode as the end, so the tree walker would break immediately without
  // capturing any text — producing an empty string on every Cmd+C.
  const range = selection.getRangeAt(0);
  const rawStartNode = range.startContainer;
  const rawEndNode = range.endContainer;

  const root =
    rawStartNode.parentElement?.closest<HTMLDivElement>('.strato-inputgroup');

  if (root === null || root === undefined) {
    return { pattern: null, text: '' };
  }

  const patternParts: Array<string> = [];
  let text = '';
  let capturing = false;
  const treeWalker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    null,
  );

  let currentNode: Node | null = treeWalker.nextNode();

  // If the endpoint is an element node rather than a text node (e.g. when
  // selectNodeContents(span) was used), step into its first child so the
  // tree-walker identity comparison can match.
  const startNode =
    rawStartNode.nodeType === Node.TEXT_NODE
      ? rawStartNode
      : rawStartNode.firstChild ?? null;
  const endNode =
    rawEndNode.nodeType === Node.TEXT_NODE
      ? rawEndNode
      : rawEndNode.firstChild ?? null;

  while (currentNode) {
    if (currentNode === startNode) {
      capturing = true;
    }

    if (capturing && checkIfValueTypeAlignment(currentNode)) {
      text += currentNode.textContent ?? '';
      const parentElement = currentNode.parentElement;
      if (parentElement?.hasAttribute('data-pattern')) {
        patternParts.push(parentElement.getAttribute('data-pattern') ?? '');
      } else if (parentElement?.dataset.valuetype !== 'expression') {
        patternParts.push(currentNode.textContent ?? '');
      }
    }

    if (currentNode === endNode) {
      break;
    }

    currentNode = treeWalker.nextNode();
  }

  const pattern =
    patternParts.length > 0 ? patternParts.join('').split('→') : null;
  return { pattern, text };
}

/**
 * Builds the clipboard text for the current selection by converting the selected
 * spin-button / expression values into a valid timeframe string.
 * Returns null if there is no selection or it does not belong to an inputgroup.
 * @internal
 */
export function buildTimeframeClipboardText(): string | null {
  const selection = window.getSelection();

  if (selection === null || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const root =
    range.startContainer.parentElement?.closest<HTMLDivElement>(
      '.strato-inputgroup',
    );

  if (root === null || root === undefined) {
    return null;
  }

  // Build text and pattern in one DOM walk. selection.toString() in Chrome only returns the text of the first
  // contenteditable element, so we use the range start/end walk in getPatternAndSelectedText.
  const { text: selectionString, pattern } =
    getPatternAndSelectedText(selection);
  return convertCopyString(selectionString, pattern);
}

/**
 * Helper function that tries to parse parts of the current selection as valid time frames
 * and if successful, copies the correctly parsed ISO timestamp or timeframe to the clipboard.
 * Must be called from a native `copy` ClipboardEvent handler so that `clipboardEvent.clipboardData`
 * is available for synchronous writing.
 * @internal
 */
export function onTimeframeCopyCapture(
  clipboardEvent: Pick<ClipboardEvent, 'preventDefault' | 'clipboardData'>,
): void {
  const clipBoardText = buildTimeframeClipboardText();

  if (clipBoardText === null) {
    return;
  }

  // Write synchronously via clipboardEvent.clipboardData and prevent the browser's native copy
  // (which would also only capture the anchor element's text due to contenteditable).
  clipboardEvent.preventDefault();
  clipboardEvent.clipboardData?.setData('text/plain', clipBoardText);
}
